/**
 * TurnManager - Zarządzanie turami i fazami gry
 *
 * Przebieg tury (3 fazy):
 * 1. FAZA STARTOWA - aktywacja efektów artefaktów
 * 2. FAZA AKCJI - zagrywanie kart, akcje główne
 * 3. FAZA KOŃCOWA - sprawdzenie limitu, odrzucenie, uzupełnienie, dobieranie
 *
 * Offline port: ArtifactEffectProcessor removed, replaced with no-op stubs.
 */

import ResourceManager from './ResourceManager.js';

// Stałe poza klasą (getter zamiast `static X = …`): Next.js kompiluje pola statyczne do bloku
// `static {}`, którego nie zna Chrome 92 w Chromecaście / Google TV.
const STATIC_PHASES = {
  START: 'START',
  ACTION: 'ACTION',
  END: 'END'
};

class TurnManager {
  /**
   * Fazy tury
   */
  static get PHASES() { return STATIC_PHASES; }

  /**
   * Rozpocznij turę gracza (FAZA STARTOWA + automatyczne przejście do ACTION)
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @returns {Object} - Status operacji
   */
  static async startPhase(game, player) {
    // 0. Wyczyść ewentualnie zablokowaną Wyprawę w Ciemno z poprzedniej tury
    if (game.wyprawaWCiemno?.active) {
      game.wyprawaWCiemno = { active: false, drawnCourageIds: [], drawnWisdomIds: [], playerId: null };
      console.log('[TurnManager] Wyczyszczono zablokowaną Wyprawę w Ciemno');
    }

    // 1. Resetuj liczniki akcji
    player.resetTurnCounters(game.gameConfig);

    // 1a. Resetuj flagę zakupu z Targowiska
    player.purchasedThisTurn = false;

    // 1b. Resetuj umiejętność bohatera
    player.resetHeroAbility();

    // 2. Resetuj pulę (zasoby tymczasowe z poprzedniej tury powinny być już zresetowane, ale dla pewności)
    if (!player.pool) {
      player.pool = { strength: 0, knowledge: 0, gold: 0 };
    } else {
      player.pool.strength = 0;
      player.pool.knowledge = 0;
      player.pool.gold = 0;
    }

    // 3. Usuń wygasłe efekty tymczasowe (na poziomie gracza)
    if (player.clearExpiredEffects) {
      player.clearExpiredEffects(game.currentTurn);
    }

    // 3a. Usuń wygasłe efekty wydarzeń z czasem trwania (na poziomie gry)
    const expiredDurationEffects = [];

    if (expiredDurationEffects.length > 0) {
      console.log(`[TurnManager] Usunięto ${expiredDurationEffects.length} wygasłych efektów wydarzeń:`,
        expiredDurationEffects.map(e => e.effectType));
    }

    // 4. NIE aktywuj automatycznie efektów PASSIVE z artefaktów
    // Wszystkie artefakty wymagają teraz ręcznego kliknięcia przez gracza
    let activatedEffects = [];
    console.log(`[TurnManager] Artefakty wymagają ręcznej aktywacji - pomijam automatyczne efekty`);

    // 4a. Tylko bonusy do liczby akcji (challenges, purchases) są automatyczne
    // Bonusy do zasobów (strength, knowledge, gold) wymagają kliknięcia
    // W tutorialu pomijamy auto-aktywację - gracz musi ręcznie aktywować artefakty
    if (game.isTutorial) {
      console.log(`[TurnManager] Tutorial mode - pomijam auto-aktywację artefaktów ALWAYS`);
    }
    try {
      // Offline: ArtifactEffectProcessor not available - return zeroed bonuses
      const alwaysBonuses = { challenges: 0, purchases: 0, strength: 0, knowledge: 0, gold: 0, cards: 0 };
      if (alwaysBonuses.challenges > 0) {
        player.extraChallenges = (player.extraChallenges || 0) + alwaysBonuses.challenges;
        console.log(`[TurnManager] Auto bonus: +${alwaysBonuses.challenges} dodatkowych wyzwań`);
      }
      if (alwaysBonuses.purchases > 0) {
        player.extraPurchases = (player.extraPurchases || 0) + alwaysBonuses.purchases;
        console.log(`[TurnManager] Auto bonus: +${alwaysBonuses.purchases} dodatkowych zakupów`);
      }

      // Bonusy do zasobów z efektów ALWAYS NIE są automatyczne
      // Gracz musi ręcznie aktywować artefakt, aby otrzymać zasoby
      if (alwaysBonuses.strength > 0 || alwaysBonuses.knowledge > 0 || alwaysBonuses.gold > 0) {
        console.log(`[TurnManager] Pominięto auto-bonus zasobów (wymagają ręcznej aktywacji): S${alwaysBonuses.strength} K${alwaysBonuses.knowledge} G${alwaysBonuses.gold}`);
      }

      // Bonus do rozmiaru ręki NIE jest automatyczny - artefakty wymagają ręcznej aktywacji
      const baseHandSize = game.gameConfig?.handSize || 5;
      player.handSize = baseHandSize;
      // Oznacz WSZYSTKIE artefakty z efektami ALWAYS jako użyte (pomijaj w tutorialu)
      if (!game.isTutorial) {
        // Offline: ArtifactEffectProcessor not available - no passive effects to process
        const alwaysEffects = [];
        if (alwaysEffects.length > 0) {
          if (!player.artifactsUsedThisTurn) player.artifactsUsedThisTurn = [];
          for (const effect of alwaysEffects) {
            if (effect.slot && !player.artifactsUsedThisTurn.includes(effect.slot)) {
              player.artifactsUsedThisTurn.push(effect.slot);
              console.log(`[TurnManager] Oznaczono artefakt ${effect.artifactName} (slot: ${effect.slot}) jako użyty (ALWAYS)`);
            }
          }
        }
      }
    } catch (error) {
      console.error('[TurnManager] Błąd przy pobieraniu bonusów ALWAYS:', error);
    }

    // 4c. Specjalna obsługa Buty Wędrowca - auto +1 challenge na START_OF_ACTION_PHASE
    // UWAGA: karta wypadła z gry w Fazie 2 przebudowy artefaktów (enabled:false,
    // plan/artefakty-symetria-6-6-6.md). Blok zostaje ŚWIADOMIE — nowa partia nie
    // rozda już tego artefaktu, ale zapisane gry offline mogą mieć go w slocie.
    const butyId = 'artifact_buty_wedrowca';
    const hasButy = player.equippedArtifacts && Object.values(player.equippedArtifacts).includes(butyId);
    if (hasButy) {
      player.extraChallenges = (player.extraChallenges || 0) + 1;
      if (!player.artifactsUsedThisTurn) player.artifactsUsedThisTurn = [];
      const butySlot = Object.entries(player.equippedArtifacts).find(([, id]) => id === butyId)?.[0];
      if (butySlot && !player.artifactsUsedThisTurn.includes(butySlot)) {
        player.artifactsUsedThisTurn.push(butySlot);
      }
      console.log(`[TurnManager] Auto-aktywacja: Buty Wędrowca (+1 dodatkowe wyzwanie)`);
    }

    // 4d. Mistrzowski Plan - wymuś zagranie jako pierwszej karty (jeśli brak klatwy w ręce)
    if (player.hand && player.hand.includes('maneuver_mistrzowski_plan')) {
      const hasCurseInHand = player.hand.some(cid => cid?.startsWith('curse_') || cid === 'curse');
      player.forcedFirstCard = hasCurseInHand ? null : 'maneuver_mistrzowski_plan';
    } else {
      player.forcedFirstCard = null;
    }

    // 5. AUTOMATYCZNE PRZEJŚCIE DO FAZY ACTION
    // Faza START jest bardzo krótka - tylko aktywacja artefaktów
    game.currentPhase = this.PHASES.ACTION;

    // 6. Log wydarzenia
    game.logEvent(
      player.userId.toString(),
      player.username,
      'turn_started',
      {
        turn: game.currentTurn,
        phase: this.PHASES.ACTION,
        activatedEffects
      }
    );

    return {
      success: true,
      phase: this.PHASES.ACTION, // Zwracamy ACTION bo tam jesteśmy
      player: {
        mainActions: player.mainActionsRemaining,
        bonusActions: player.bonusActionsThisTurn,
        purchases: player.purchasesRemaining,
        challenges: player.challengesRemaining,
        pool: player.pool
      },
      activatedEffects
    };
  }

  /**
   * Faza akcji - główna część tury
   * Gracz wykonuje akcje:
   * - Zagrywanie kart z ręki
   * - Darmowe Akcje (Strefa Miejska)
   * - 2 Akcje Główne (Zakup / Wyzwanie / Podjęcie Zlecenia)
   *
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @returns {Object}
   */
  static async actionPhase(game, player) {
    // Zmień fazę
    game.currentPhase = this.PHASES.ACTION;

    return {
      success: true,
      phase: this.PHASES.ACTION,
      availableActions: {
        canPlayCards: player.hand.length > 0,
        canUseCityZone: true,
        mainActions: player.mainActionsRemaining + player.bonusActionsThisTurn,
        purchases: player.purchasesRemaining,
        challenges: player.challengesRemaining
      }
    };
  }

  /**
   * Zakończ turę gracza (FAZA KOŃCOWA)
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @returns {Object} - Status operacji
   */
  static async endPhase(game, player) {
    // Zmień fazę
    game.currentPhase = this.PHASES.END;

    const summary = {
      phase: this.PHASES.END,
      poolBeforeReset: player.pool ? { ...player.pool } : { strength: 0, knowledge: 0, gold: 0 },
      bankBeforeLimit: { ...player.resources },
      lostFromPool: {},
      lostFromBank: {},
      cardsDiscarded: 0,
      cardsDrawn: 0,
      endOfTurnEffects: []
    };

    // 0. Aplikuj efekty END_OF_TURN z artefaktów
    // ZMIANA: Wyłączamy automatyczne efekty END_OF_TURN - artefakty muszą być klikane ręcznie
    /*
    try {
      const endTurnEffects = await ArtifactEffectProcessor.applyPassiveEffectsForTrigger(
        game,
        player,
        ['END_OF_TURN']
      );
      summary.endOfTurnEffects = endTurnEffects;
      console.log(`[TurnManager] Zaaplikowano ${endTurnEffects.length} efektów END_OF_TURN`);
    } catch (error) {
      console.error('[TurnManager] Błąd przy aplikowaniu efektów END_OF_TURN:', error);
    }
    */

    // 0b. Horda Pustkowi: nieużyte żetony furii przepadają na końcu tury
    if (player.faction === 'horda_pustkowi' && player.tokens?.fury > 0) {
      console.log(`[TurnManager] Clearing ${player.tokens.fury} unspent fury tokens for ${player.username} at end of turn`);
      player.tokens.fury = 0;
    }

    // 1. SMART BANKING - automatyczny transfer z puli do banku (z zachowaniem limitu)
    const transferResult = ResourceManager.transferAllToBank(player, game);
    summary.transferredToBank = transferResult.transferred;
    summary.lostFromPool = transferResult.overflow;

    // 2. Sprawdź limit Skarbca/Banku (max 10 każdego zasobu)
    summary.lostFromBank = ResourceManager.applySkarbiecLimit(player, game);

    // 2. Odrzuć wszystkie zagrane karty
    if (player.discardPlayed) {
      const playedCount = player.played.length;
      player.discardPlayed();
      summary.cardsDiscarded += playedCount;
    }

    // 3. Odrzuć całą rękę
    if (player.discardHand) {
      const handCount = player.hand.length;
      player.discardHand();
      summary.cardsDiscarded += handCount;
    }

    // 4. Uzupełnij braki na Targowisku i Szlakach
    game.refillAllZones();

    // 5. Dobierz nowe karty (do limitu ręki)
    const handLimit = player.handSize || game.gameConfig?.handSize || 5;
    if (player.drawCards) {
      const drawn = player.drawCards(handLimit);
      summary.cardsDrawn = drawn.length;
    }

    // 6. Sprawdź warunki końca gry
    const gameEnded = game.checkGameEnd();
    summary.gameEnded = gameEnded;

    if (gameEnded) {
      game.calculateWinner();
      summary.winner = game.winner;
    }

    // 7. Log wydarzenia
    game.logEvent(
      player.userId.toString(),
      player.username,
      'turn_ended',
      {
        turn: game.currentTurn,
        summary
      }
    );

    summary.success = true;
    return summary;
  }

  /**
   * Zakończ turę gracza BEZ uzupełniania szlaków (obsługa wydarzeń w socket handlerze)
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @returns {Object} - Status operacji
   */
  static async endPhaseWithoutRefill(game, player) {
    // Zmień fazę
    game.currentPhase = this.PHASES.END;

    const summary = {
      phase: this.PHASES.END,
      poolBeforeReset: player.pool ? { ...player.pool } : { strength: 0, knowledge: 0, gold: 0 },
      bankBeforeLimit: { ...player.resources },
      lostFromPool: {},
      lostFromBank: {},
      cardsDiscarded: 0,
      cardsDrawn: 0,
      endOfTurnEffects: []
    };

    // 0. Aplikuj efekty END_OF_TURN z artefaktów
    // ZMIANA: Wyłączamy automatyczne efekty END_OF_TURN - artefakty muszą być klikane ręcznie
    /*
    try {
      const endTurnEffects = await ArtifactEffectProcessor.applyPassiveEffectsForTrigger(
        game,
        player,
        ['END_OF_TURN']
      );
      summary.endOfTurnEffects = endTurnEffects;
      console.log(`[TurnManager] Zaaplikowano ${endTurnEffects.length} efektów END_OF_TURN`);
    } catch (error) {
      console.error('[TurnManager] Błąd przy aplikowaniu efektów END_OF_TURN:', error);
    }
    */

    // 0b. Horda Pustkowi: nieużyte żetony furii przepadają na końcu tury
    if (player.faction === 'horda_pustkowi' && player.tokens?.fury > 0) {
      console.log(`[TurnManager] Clearing ${player.tokens.fury} unspent fury tokens for ${player.username} at end of turn`);
      player.tokens.fury = 0;
    }

    // 1. SMART BANKING - automatyczny transfer z puli do banku (z zachowaniem limitu)
    const transferResult = ResourceManager.transferAllToBank(player, game);
    summary.transferredToBank = transferResult.transferred;
    summary.lostFromPool = transferResult.overflow;

    // 2. Sprawdź limit Skarbca/Banku (max 10 każdego zasobu)
    summary.lostFromBank = ResourceManager.applySkarbiecLimit(player, game);

    // 3. Odrzuć wszystkie zagrane karty
    if (player.discardPlayed) {
      const playedCount = player.played.length;
      player.discardPlayed();
      summary.cardsDiscarded += playedCount;
    }

    // 4. Odrzuć całą rękę
    if (player.discardHand) {
      const handCount = player.hand.length;
      player.discardHand();
      summary.cardsDiscarded += handCount;
    }

    // UWAGA: Nie uzupełniamy szlaków tutaj - to robi socket handler z wykrywaniem wydarzeń

    // Diagnostyka - ile kart w obiegu
    const totalCirculating = player.hand.length + player.deck.length + player.discard.length + player.played.length;
    const equippedCount = Object.values(player.equippedArtifacts || {}).filter(v => v !== null).length;
    const destroyedCount = (player.destroyed || []).length;
    console.log(`[END_TURN_DRAW] ${player.username}: przed doborem - hand=${player.hand.length}, deck=${player.deck.length}, discard=${player.discard.length}, played=${player.played.length}, total_w_obiegu=${totalCirculating}, equipped=${equippedCount}, destroyed=${destroyedCount}`);

    // 5. Dobierz nowe karty (do limitu ręki)
    const handLimit = player.handSize || game.gameConfig?.handSize || 5;
    if (player.drawCards) {
      const drawn = player.drawCards(handLimit);
      summary.cardsDrawn = drawn.length;
      console.log(`[END_TURN_DRAW] ${player.username}: po doborze - hand=${player.hand.length}, deck=${player.deck.length}, discard=${player.discard.length} (dobrano ${drawn.length}/${handLimit})`);

      // SAFETY NET: jeśli dobrano mniej niż limit, spróbuj jeszcze raz (na wypadek gdyby reshuffle nie zadziałał)
      if (player.hand.length < handLimit && (player.deck.length > 0 || player.discard.length > 0)) {
        console.log(`[END_TURN_DRAW] ${player.username}: SAFETY - dobrano tylko ${player.hand.length}/${handLimit}, próbuję dobrać resztę`);
        const extraDraw = player.drawCards(handLimit - player.hand.length);
        summary.cardsDrawn += extraDraw.length;
        console.log(`[END_TURN_DRAW] ${player.username}: po dodatkowym doborze - hand=${player.hand.length}, deck=${player.deck.length}, discard=${player.discard.length} (extra ${extraDraw.length})`);
      }
    }

    // 6. Sprawdź warunki końca gry
    const gameEnded = game.checkGameEnd();
    summary.gameEnded = gameEnded;

    if (gameEnded) {
      game.calculateWinner();
      summary.winner = game.winner;
    }

    // 7. Log wydarzenia
    game.logEvent(
      player.userId.toString(),
      player.username,
      'turn_ended',
      {
        turn: game.currentTurn,
        summary
      }
    );

    summary.success = true;
    return summary;
  }

  /**
   * Przejdź do następnego gracza
   * @param {Object} game - Obiekt gry
   * @returns {Object} - Nowy aktualny gracz
   */
  static nextPlayer(game) {
    const previousPlayerIndex = game.currentPlayerIndex;

    // Użyj metody z modelu Game
    game.nextPlayer();

    const currentPlayer = game.getCurrentPlayer();

    // Check if we completed a round (went back to first player)
    if (game.currentPlayerIndex <= previousPlayerIndex) {
      console.log(`[TurnManager] New round started! Resetting passive usage counters.`);
      // Reset passive usage counters for all players
      for (const player of game.players) {
        if (player.passiveUsageThisRound) {
          player.passiveUsageThisRound = new Map();
        }
      }
    }

    // Log
    game.logEvent(
      currentPlayer.userId.toString(),
      currentPlayer.username,
      'player_turn_started',
      {
        turn: game.currentTurn,
        playerIndex: game.currentPlayerIndex
      }
    );

    return currentPlayer;
  }

  /**
   * Wykonaj pełną turę gracza (wszystkie 3 fazy)
   * UWAGA: To jest uproszczona wersja - w prawdziwej grze ACTION będzie zarządzana przez Socket.io
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @param {Function} actionCallback - Funkcja wywoływana w fazie akcji (opcjonalna)
   * @returns {Object}
   */
  static async executeFullTurn(game, player, actionCallback = null) {
    const turnSummary = {
      turn: game.currentTurn,
      player: player.username,
      phases: {}
    };

    // FAZA 1: STARTOWA
    turnSummary.phases.start = await this.startPhase(game, player);

    // FAZA 2: AKCJI
    turnSummary.phases.action = await this.actionPhase(game, player);

    // Jeśli podano callback, wykonaj akcje
    if (actionCallback && typeof actionCallback === 'function') {
      try {
        turnSummary.phases.action.playerActions = await actionCallback(game, player);
      } catch (error) {
        turnSummary.phases.action.error = error.message;
      }
    }

    // FAZA 3: KOŃCOWA
    turnSummary.phases.end = await this.endPhase(game, player);

    return turnSummary;
  }

  /**
   * Sprawdź czy gracz może zakończyć turę
   * @param {Object} player - Obiekt gracza
   * @returns {Object} - { canEnd: Boolean, reason?: String }
   */
  static canEndTurn(player) {
    // Gracz zawsze może zakończyć turę, ale możemy ostrzec o niewykorzystanych zasobach
    const warnings = [];

    if (player.mainActionsRemaining > 0 || player.bonusActionsThisTurn > 0) {
      warnings.push('Masz niewykorzystane akcje główne');
    }

    if (player.purchasesRemaining > 0) {
      warnings.push('Masz niewykorzystany zakup');
    }

    if (player.challengesRemaining > 0) {
      warnings.push('Masz niewykorzystane wyzwanie');
    }

    const resourceTotal = player.resources.strength + player.resources.knowledge + player.resources.gold;
    if (resourceTotal > 0) {
      warnings.push(`Masz ${resourceTotal} niewydanych zasobów`);
    }

    return {
      canEnd: true,
      warnings
    };
  }

  /**
   * Pobierz status aktualnej fazy
   * @param {Object} game - Obiekt gry
   * @param {Object} player - Obiekt gracza
   * @returns {Object}
   */
  static getPhaseStatus(game, player) {
    return {
      currentPhase: game.currentPhase,
      turn: game.currentTurn,
      player: {
        username: player.username,
        handSize: player.hand.length,
        deckSize: player.deck.length,
        discardSize: player.discard.length,
        resources: { ...player.resources },
        actions: {
          main: player.mainActionsRemaining,
          bonus: player.bonusActionsThisTurn,
          purchases: player.purchasesRemaining,
          challenges: player.challengesRemaining
        }
      },
      canEndTurn: this.canEndTurn(player)
    };
  }
}

export default TurnManager;
